import { ToneIndex } from "./core";
import { Tonality } from "./scales";


/**
 * Returns the scale note names for the given tonality.
 * The note names obey two rules:
 * 1. They follow the expected sequential letters (e.g. C D E F G A B)
 *    starting from the tonality's center (using its letter part).
 * 2. They add sharps or flats so the name matches the actual pitch.
 */
export function produceScaleNoteNames(tonality: Tonality): string[] {
    // Get the active scale steps from the interval pattern.
    const activeSteps = tonality.scalePattern.pattern.toActiveSteps();
    // Compute the scale degrees (ordered from the center) by adding each step.
    const scaleDegrees: ToneIndex[] = activeSteps.map(step => (tonality.center.up(step).index));

    // Musical letter cycle (for expected note letters)
    const letters = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    // Use the first character of center.getName() as the starting letter.
    let startLetter = tonality.center.getName()[0];
    let startIdx = letters.indexOf(startLetter);
    if (startIdx === -1) {
        startIdx = 0; // fallback to C if not found
    }
    // Expected letters in order for the scale degrees.
    const expectedLetters = scaleDegrees.map((_, i) => letters[(startIdx + i) % letters.length]);

    // For each scale degree, decide the accidental needed.
    return scaleDegrees.map((pitch, idx) => {
        const expected = expectedLetters[idx];
        return noteNameFor(expected, pitch);
    });
}

/**
 * Given an expected letter (e.g. "F") and an actual pitch (0-11),
 * returns the note name (adding sharps or flats as needed).
 *
 * For each letter we assume the "natural" pitch:
 *   C -> 0, D -> 2, E -> 4, F -> 5, G -> 7, A -> 9, B -> 11.
 *
 * The difference between the actual pitch and the natural pitch tells us how many
 * accidentals (sharps if positive, flats if negative) are required.
 */
function noteNameFor(expected: string, pitch: number): string {
    const naturalMap: { [key: string]: number } = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

    const natural = naturalMap[expected];
    // Compute the minimal difference between pitch and natural (in range -6...+6).
    let diff = ((pitch - natural) % 12 + 12) % 12;
    if (diff > 6) diff -= 12; // use negative difference if that is shorter

    if (diff === 0) return expected;
    if (diff > 0) return expected + "#".repeat(diff);
    // for diff negative we add flats
    return expected + "b".repeat(-diff);
}